// 📋 Hook para gestionar menús dinámicos
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient, tokenStore } from '../api/client';
import type { MenusResponse } from '@/types/menu';

/**
 * Obtener menús con permisos desde el backend
 */
// Copia del menú por usuario: sin señal la app (y el shell de campo) arranca con el último menú
// conocido. En el dispositivo solo si la sesión se conserva; si no, solo en esta pestaña.
const MENUS_KEY = 'erp_menus_cache';
function usuarioDelToken(): string {
  try { return (JSON.parse(atob((tokenStore.getAccess() ?? '').split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as { user_id?: string }).user_id ?? ''; }
  catch { return ''; }
}
function guardarCopia(data: MenusResponse) {
  try {
    const v = JSON.stringify({ uid: usuarioDelToken(), data });
    sessionStorage.setItem(MENUS_KEY, v);
    if (tokenStore.recordar()) localStorage.setItem(MENUS_KEY, v); else localStorage.removeItem(MENUS_KEY);
  } catch { /* almacenamiento lleno */ }
}
function leerCopia(): MenusResponse | null {
  try {
    const c = JSON.parse(sessionStorage.getItem(MENUS_KEY) ?? localStorage.getItem(MENUS_KEY) ?? 'null') as { uid: string; data: MenusResponse } | null;
    return c && c.uid && c.uid === usuarioDelToken() ? c.data : null;   // nunca el menú de otro usuario
  } catch { return null; }
}

async function fetchMenus(): Promise<MenusResponse> {
  // RPC: árbol de menús del usuario actual filtrado por sus asignaciones de perfil
  try {
    const { data } = await apiClient.post<MenusResponse>('/rpc/get_menus', {});
    guardarCopia(data);
    return data;
  } catch (e) {
    const x = e as { response?: unknown; code?: string; message?: string };
    const sinRed = !x?.response && (x?.code === 'ERR_NETWORK' || x?.message === 'Network Error');
    const copia = sinRed ? leerCopia() : null;
    if (copia) return copia;
    throw e;
  }
}

/**
 * Hook para obtener menús dinámicos con permisos
 */
// Nombre en el idioma activo: traducciones[lang] (o su idioma base) y, si no hay, el nombre
// original. En el ERP los menús no traen traducciones y se ven igual que siempre.
const traducir = <T extends { nombre: string; traducciones?: Record<string, string> | null }>(x: T, lang: string): T => {
  const t = x.traducciones;
  const nombre = (t && (t[lang] ?? t[lang.split('-')[0]])) || x.nombre;
  return nombre === x.nombre ? x : { ...x, nombre };
};

export function useMenus() {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'es';
  const select = useCallback((d: MenusResponse): MenusResponse => ({
    ...d,
    perfil: d.perfil ? traducir(d.perfil as unknown as { nombre: string; traducciones?: Record<string, string> | null }, lang) as unknown as MenusResponse['perfil'] : d.perfil,
    menus: (d.menus || []).map((m) => ({
      ...traducir(m, lang),
      submenus: (m.submenus || []).map((s) => ({
        ...traducir(s, lang),
        opciones: (s.opciones || []).map((o) => traducir(o, lang)),
      })),
    })),
  }), [lang]);
  return useQuery({
    queryKey: ['menus'],
    queryFn: fetchMenus,
    select,
    staleTime: 10 * 60 * 1000, // 10 minutos
    retry: 1,
    networkMode: 'always',   // sin red responde la copia local (TanStack, si no, la pausaría)
  });
}
